import { useCallback, useEffect, useRef, useState } from "react"

type InlineColorPickerProps = {
  variableName: string
  currentValue: string
  onUpdate: (variableName: string, value: string) => void
}

const ZOOM_LEVELS = [1, 2, 3] as const
type ZoomLevel = (typeof ZOOM_LEVELS)[number]

export const InlineColorPicker = ({ variableName, currentValue, onUpdate }: InlineColorPickerProps) => {
  const [hue, setHue] = useState(0)
  const [saturation, setSaturation] = useState(0)
  const [lightness, setLightness] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(1)
  const squareRef = useRef<HTMLDivElement>(null)
  const dragStartRangeRef = useRef<{ sMin: number; sMax: number; lMin: number; lMax: number } | null>(null)

  // Parse initial HSL values and format to 2 decimal places
  useEffect(() => {
    const hslMatch = currentValue.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
    if (hslMatch) {
      setHue(parseFloat(parseFloat(hslMatch[1] ?? "").toFixed(2)))
      setSaturation(parseFloat(parseFloat(hslMatch[2] ?? "").toFixed(2)))
      setLightness(parseFloat(parseFloat(hslMatch[3] ?? "").toFixed(2)))
    }
  }, [currentValue])

  const updateColor = useCallback(
    (h: number, s: number, l: number) => {
      // Format all values to 2 decimal places
      const formattedH = parseFloat(h.toFixed(2))
      const formattedS = parseFloat(s.toFixed(2))
      const formattedL = parseFloat(l.toFixed(2))
      const newValue = `${formattedH} ${formattedS}% ${formattedL}%`
      onUpdate(variableName, newValue)
    },
    [variableName, onUpdate],
  )

  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHue = parseFloat(parseFloat(e.target.value).toFixed(2))
    setHue(newHue)
    updateColor(newHue, saturation, lightness)
  }

  // Calculate visible range based on zoom level and current selection
  const getVisibleRange = useCallback(() => {
    if (zoomLevel === 1) {
      return {
        sMin: 0,
        sMax: 100,
        lMin: 0,
        lMax: 100,
      }
    }

    const range = 100 / zoomLevel
    const sCenter = saturation
    const lCenter = lightness

    const sMin = Math.max(0, sCenter - range / 2)
    const sMax = Math.min(100, sCenter + range / 2)
    const lMin = Math.max(0, lCenter - range / 2)
    const lMax = Math.min(100, lCenter + range / 2)

    return { sMin, sMax, lMin, lMax }
  }, [zoomLevel, saturation, lightness])

  const getPositionFromHSL = (s: number, l: number, width: number, height: number) => {
    const range = getVisibleRange()
    const sRange = range.sMax - range.sMin
    const lRange = range.lMax - range.lMin

    const x = ((s - range.sMin) / sRange) * width
    const y = ((range.lMax - l) / lRange) * height
    return { x, y }
  }

  const getHSLFromPosition = (x: number, y: number, width: number, height: number, useDragRange = false) => {
    // Use the drag start range if dragging, otherwise use current range
    const range = useDragRange && dragStartRangeRef.current ? dragStartRangeRef.current : getVisibleRange()
    const sRange = range.sMax - range.sMin
    const lRange = range.lMax - range.lMin

    const s = range.sMin + (x / width) * sRange
    const l = range.lMax - (y / height) * lRange

    // Round to 2 decimal places
    return {
      s: parseFloat(Math.max(0, Math.min(100, s)).toFixed(2)),
      l: parseFloat(Math.max(0, Math.min(100, l)).toFixed(2)),
    }
  }

  const handleZoomToggle = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel)
    const nextIndex = (currentIndex + 1) % ZOOM_LEVELS.length
    setZoomLevel(ZOOM_LEVELS[nextIndex]!)
  }

  const handleSquareMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!squareRef.current) return
    // Capture the visible range at drag start to keep it fixed during drag
    dragStartRangeRef.current = getVisibleRange()
    setIsDragging(true)
    const rect = squareRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const { s, l } = getHSLFromPosition(x, y, rect.width, rect.height, true)
    setSaturation(s)
    setLightness(l)
    updateColor(hue, s, l)
  }

  const handleSquareMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !squareRef.current) return
    const rect = squareRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
    const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))
    const { s, l } = getHSLFromPosition(x, y, rect.width, rect.height, true)
    setSaturation(s)
    setLightness(l)
    updateColor(hue, s, l)
  }

  const handleSquareMouseUp = () => {
    setIsDragging(false)
    dragStartRangeRef.current = null
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !squareRef.current) return
      const rect = squareRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))
      const { s, l } = getHSLFromPosition(x, y, rect.width, rect.height, true)
      setSaturation(s)
      setLightness(l)
      updateColor(hue, s, l)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      dragStartRangeRef.current = null
    }

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, hue, saturation, lightness, updateColor])

  const squareSize = 200
  const circleSize = 12
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Draw the color square on canvas for accurate HSL representation
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const width = squareSize
    const height = squareSize
    const range = getVisibleRange()

    // Use ImageData for better performance
    const imageData = ctx.createImageData(width, height)
    const data = imageData.data

    // Draw the 2D color space: X = saturation, Y = lightness (zoomed view)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Map pixel position to HSL values within visible range
        const sRange = range.sMax - range.sMin
        const lRange = range.lMax - range.lMin
        const s = range.sMin + (x / width) * sRange
        const l = range.lMax - (y / height) * lRange

        // Convert HSL to RGB
        const h = hue / 360
        const sNorm = s / 100
        const lNorm = l / 100

        let r: number, g: number, b: number

        if (sNorm === 0) {
          r = g = b = lNorm
        } else {
          const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1
            if (t > 1) t -= 1
            if (t < 1 / 6) return p + (q - p) * 6 * t
            if (t < 1 / 2) return q
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
            return p
          }

          const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm
          const p = 2 * lNorm - q

          r = hue2rgb(p, q, h + 1 / 3)
          g = hue2rgb(p, q, h)
          b = hue2rgb(p, q, h - 1 / 3)
        }

        const index = (y * width + x) * 4
        data[index] = Math.round(r * 255)
        data[index + 1] = Math.round(g * 255)
        data[index + 2] = Math.round(b * 255)
        data[index + 3] = 255
      }
    }

    ctx.putImageData(imageData, 0, 0)
  }, [hue, squareSize, getVisibleRange])

  return (
    <div className="mt-2 space-y-3">
      {/* Color Square */}
      <div className="flex items-start gap-2">
        <div className="relative">
          <div
            ref={squareRef}
            className="relative cursor-crosshair overflow-hidden rounded-lg border border-secondary-700"
            style={{
              width: squareSize,
              height: squareSize,
            }}
            onMouseDown={handleSquareMouseDown}
            onMouseMove={handleSquareMouseMove}
            onMouseUp={handleSquareMouseUp}
            onMouseLeave={handleSquareMouseUp}
          >
            <canvas
              ref={canvasRef}
              width={squareSize}
              height={squareSize}
              className="pointer-events-none absolute inset-0"
            />
            {/* Circle indicator */}
            {(() => {
              const { x: circleX, y: circleY } = getPositionFromHSL(saturation, lightness, squareSize, squareSize)
              return (
                <div
                  className="pointer-events-none absolute rounded-full border-2 border-white shadow-lg"
                  style={{
                    width: circleSize,
                    height: circleSize,
                    left: circleX - circleSize / 2,
                    top: circleY - circleSize / 2,
                    backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
                  }}
                />
              )
            })()}
          </div>
        </div>
        {/* Zoom Control */}
        <div className="flex flex-col gap-2">
          <button
            onClick={handleZoomToggle}
            className="rounded border border-secondary-700 bg-secondary-800 px-3 py-1.5 text-xs text-secondary-300 transition-colors hover:bg-secondary-700 hover:text-secondary-50"
            title={`Zoom: ${zoomLevel}x (Click to cycle)`}
          >
            {zoomLevel}x
          </button>
          {zoomLevel > 1 && (
            <div className="text-xs text-secondary-400">
              <div>
                S: {getVisibleRange().sMin.toFixed(1)}% - {getVisibleRange().sMax.toFixed(1)}%
              </div>
              <div>
                L: {getVisibleRange().lMin.toFixed(1)}% - {getVisibleRange().lMax.toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hue Slider */}
      <div>
        <label className="mb-1 block text-xs text-secondary-300">Hue</label>
        <div className="relative">
          <input
            type="range"
            min="0"
            max="360"
            step="1"
            value={hue}
            onChange={handleHueChange}
            className="h-2 w-full cursor-pointer appearance-none rounded-lg"
            style={{
              background: `linear-gradient(to right,
                hsl(0, 100%, 50%) 0%,
                hsl(60, 100%, 50%) 16.66%,
                hsl(120, 100%, 50%) 33.33%,
                hsl(180, 100%, 50%) 50%,
                hsl(240, 100%, 50%) 66.66%,
                hsl(300, 100%, 50%) 83.33%,
                hsl(360, 100%, 50%) 100%
              )`,
            }}
          />
          <div className="mt-1 text-xs text-secondary-400">{hue.toFixed(0)}°</div>
        </div>
      </div>
    </div>
  )
}
